import React from 'react';

import { DocumentViewer } from '@components/DocumentViewer';

import type { Document } from '@models/document';

interface State {
  isOpen: boolean;
  onClose?: () => void;
  document: Document | null;
}

export enum DocumentViewerActions {
  Reset = 'reset',
  View = 'view',
}

type Payload = {
  [DocumentViewerActions.Reset]: undefined;
  [DocumentViewerActions.View]: {
    document: Document;
  };
};

const initialState: State = {
  /** Is the document viewer open? */
  isOpen: false,
  /** Document to view */
  document: null,
};

function reducer(state: State, action: Action<Payload>) {
  switch (action.type) {
    case DocumentViewerActions.Reset:
      return initialState;
    case DocumentViewerActions.View:
      return {
        ...state,
        document: action.document,
        isOpen: true,
      };
    default:
      return state;
  }
}

export const DocumentViewerContext = React.createContext<{
  state: State;
  dispatch: React.Dispatch<Action<Payload>>;
}>({ state: initialState, dispatch: () => null });
DocumentViewerContext.displayName = 'DocumentViewerContext';

export const DocumentViewerProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children = null }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const value = React.useMemo(() => ({ state, dispatch }), [state]);

  return (
    <DocumentViewerContext.Provider value={value}>
      {state.document && (
        <DocumentViewer doc={state.document} open={state.isOpen} />
      )}

      {children}
    </DocumentViewerContext.Provider>
  );
};
